import { useMemo, useState } from "react";
import { IconChevronDown, IconChevronRight, IconEyeOff, IconGripVertical, IconSearch } from "@tabler/icons-react";
import { Badge, Card, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { Catalogo, RepDefinicion } from "../services/reporteria.api";
import * as ops from "../state/builder-ops";

/**
 * Picker del catálogo (whitelist del usuario · §5.2). Las columnas que "lucen
 * como id/FK" vienen ocultas (RN-20) y NO se listan. Al elegir una columna se
 * agrega su tabla (si faltaba) y la columna a la definición.
 */
export function CatalogoTablas({
  catalogo,
  def,
  setDef,
}: {
  catalogo: Catalogo;
  def: RepDefinicion;
  setDef: (f: (d: RepDefinicion) => RepDefinicion) => void;
}) {
  const [q, setQ] = useState("");
  const [abiertas, setAbiertas] = useState<Set<string>>(() => new Set());

  const filtro = q.trim().toLowerCase();
  const tablas = useMemo(
    () =>
      catalogo.tablas
        .map((t) => ({
          ...t,
          // RN-20: se ocultan del picker las columnas id/FK.
          columnas: t.columnas.filter((c) => !c.ocultaEnPicker),
        }))
        .filter((t) => t.columnas.length > 0)
        .map((t) => ({
          ...t,
          columnas: filtro
            ? t.columnas.filter(
                (c) => c.etiqueta.toLowerCase().includes(filtro) || t.etiqueta.toLowerCase().includes(filtro)
              )
            : t.columnas,
        }))
        .filter((t) => t.columnas.length > 0 || t.etiqueta.toLowerCase().includes(filtro)),
    [catalogo.tablas, filtro]
  );

  function toggleColumna(tablaToken: string, colToken: string, etiqueta: string) {
    setDef((d) => {
      let next = ops.addTabla(d, tablaToken);
      const alias = ops.aliasDeToken(next, tablaToken)!;
      if (ops.hasColumna(next, alias, colToken)) {
        const idx = next.columnas.findIndex((c) => c.tablaAlias === alias && c.token === colToken);
        next = ops.removeColumna(next, idx);
        // Si la tabla quedó sin columnas y no es la base, se puede quitar.
        if (next.columnas.every((c) => c.tablaAlias !== alias) && ops.baseAlias(next) !== alias) {
          next = ops.removeTabla(next, alias);
        }
        return next;
      }
      return ops.addColumna(next, alias, colToken, etiqueta);
    });
  }

  function toggleAbierta(token: string) {
    setAbiertas((prev) => {
      const s = new Set(prev);
      s.has(token) ? s.delete(token) : s.add(token);
      return s;
    });
  }

  return (
    <Card className="flex h-full flex-col p-3">
      <Text fw={600} className="mb-2">
        Datos disponibles
      </Text>
      <div className="relative mb-2">
        <IconSearch size={15} className="absolute left-2 top-2.5 text-muted-foreground" />
        <input
          className="h-8 w-full rounded-md border border-border bg-background pl-7 pr-2 text-sm"
          placeholder="Buscar tabla o columna…"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
        {tablas.length === 0 && (
          <Text size="sm" c="dimmed" className="p-2">
            Sin coincidencias.
          </Text>
        )}
        {tablas.map((t) => {
          const abierta = filtro !== "" || abiertas.has(t.token);
          const alias = ops.aliasDeToken(def, t.token);
          const seleccionadas = alias ? def.columnas.filter((c) => c.tablaAlias === alias).length : 0;
          return (
            <div key={t.token} className="rounded-md border border-border">
              <button
                type="button"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/x-rep-tabla", t.token);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => toggleAbierta(t.token)}
                title="Arrástrala al lienzo de relaciones para agregarla"
                className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-sm font-medium hover:bg-muted"
              >
                {abierta ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
                <span className="flex-1 truncate">{t.etiqueta}</span>
                {seleccionadas > 0 && (
                  <Badge size="xs" variant="light">
                    {seleccionadas}
                  </Badge>
                )}
                <IconGripVertical size={14} className="shrink-0 text-muted-foreground/50" aria-hidden />
              </button>
              {abierta && (
                <div className="space-y-0.5 border-t border-border p-1">
                  {t.columnas.map((c) => {
                    const sel = alias ? ops.hasColumna(def, alias, c.token) : false;
                    return (
                      <button
                        key={c.token}
                        type="button"
                        onClick={() => toggleColumna(t.token, c.token, c.etiqueta)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted",
                          sel && "bg-multiva-50 dark:bg-multiva-900/30"
                        )}
                      >
                        <span
                          className={cn(
                            "flex h-4 w-4 items-center justify-center rounded border text-[10px]",
                            sel ? "border-multiva-600 bg-multiva-600 text-white" : "border-border"
                          )}
                        >
                          {sel ? "✓" : ""}
                        </span>
                        <span className="flex-1 truncate">{c.etiqueta}</span>
                        {c.enmascarada && (
                          <IconEyeOff size={14} className="text-muted-foreground" aria-label="Enmascarada" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
